import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, X, Check, Sparkles, AlertTriangle, ShieldCheck } from 'lucide-react';
import { SectionTitle, PremiumButton, GoldDivider, FlowerMotif } from './premium/Decor';

const BEFORE = [
  { hi: 'जीवन के निर्णयों में उलझन', en: 'Confusion in life decisions' },
  { hi: 'करियर / नौकरी में रुकावट', en: 'Career stuck, no growth' },
  { hi: 'विवाह और रिश्तों में तनाव', en: 'Tension in relationships and marriage' },
  { hi: 'बार-बार होती असफलताएँ', en: 'Repeated failures and setbacks' },
  { hi: 'पैसा आता है पर टिकता नहीं', en: 'Money comes but slips away' },
  { hi: 'भविष्य को लेकर अनिश्चितता', en: 'Uncertainty about the future' },
];
const AFTER = [
  { hi: 'हर निर्णय में स्पष्टता', en: 'Clarity in every decision' },
  { hi: 'सही करियर मार्ग और प्रमोशन', en: 'Right career path & promotions' },
  { hi: 'मधुर रिश्ते, सुखद विवाह', en: 'Harmonious relationships & blissful marriage' },
  { hi: 'सही समय पर सही कदम', en: 'Right action at the right time' },
  { hi: 'धन योगों की पहचान', en: 'Identifying wealth-bringing yogas' },
  { hi: 'अगले 5 साल का स्पष्ट रोडमैप', en: 'Clear 5-year roadmap' },
];

const BeforeAfterSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="before-after-section"
      className="relative bg-gradient-to-b from-brand-cream via-white to-brand-cream py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 w-[500px] h-[500px] bg-mandala-large bg-no-repeat bg-center opacity-25 pointer-events-none -translate-x-1/2" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'आपका रूपांतरण' : 'Your Transformation'}
          title={isHi ? 'कुंडली से पहले vs कुंडली के बाद' : 'Before Kundali vs After Kundali'}
          subtitle={isHi
            ? 'देखिए, जीवन कैसे बदल जाता है जब आपको ब्रह्मांड का सही नक्शा मिल जाए।'
            : 'See how life transforms once you have the precise cosmic map in your hands.'}
          language={language}
        />

        <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-10 max-w-5xl mx-auto items-stretch">
          {/* BEFORE column */}
          <div className="relative bg-gradient-to-br from-[#3a1d1d] via-[#241010] to-[#1a0808] rounded-3xl p-8 md:p-10 text-white border-2 border-red-900/40 shadow-premium overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at 30% 20%, rgba(229,57,53,0.25) 0%, transparent 55%)',
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-red-900/40 border border-red-500/30 rounded-full px-3 py-1 mb-5">
                <AlertTriangle className="w-3.5 h-3.5 text-red-300" />
                <span className={`text-xs font-bold uppercase tracking-[0.2em] text-red-200 ${isHi ? 'font-hindi' : ''}`}>
                  {isHi ? 'पहले' : 'Before'}
                </span>
              </div>
              <h3 className={`font-playfair font-bold text-2xl md:text-3xl text-white mb-2 ${isHi ? 'font-hindi-serif' : ''}`}>
                {isHi ? 'जीवन भटकाव में' : 'Life In Confusion'}
              </h3>
              <p className={`text-white/65 text-sm mb-6 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? 'अंधेरे में चलना, हर निर्णय अनिश्चित।' : 'Walking in the dark, every decision uncertain.'}
              </p>
              <ul className="space-y-3.5">
                {BEFORE.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500/20 text-red-300 flex items-center justify-center mt-0.5 border border-red-500/30">
                      <X className="w-3.5 h-3.5" />
                    </span>
                    <span className={`text-white/85 text-sm md:text-base ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {isHi ? item.hi : item.en}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* AFTER column */}
          <div className="relative bg-gradient-to-br from-brand-gold-light/40 via-white to-brand-cream rounded-3xl p-8 md:p-10 text-brand-text-dark border-2 border-brand-gold shadow-gold-glow overflow-hidden">
            <div
              className="absolute inset-0 opacity-50 pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle at 70% 80%, rgba(212,175,55,0.30) 0%, transparent 55%)',
              }}
            />
            <div className="absolute top-3 right-3 opacity-30">
              <FlowerMotif className="w-10 h-10 text-brand-gold" />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-brand-maroon text-white rounded-full px-3 py-1 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-brand-gold-light" />
                <span className={`text-xs font-bold uppercase tracking-[0.2em] ${isHi ? 'font-hindi' : ''}`}>
                  {isHi ? 'के बाद' : 'After'}
                </span>
              </div>
              <h3 className={`font-playfair font-bold text-2xl md:text-3xl text-brand-maroon mb-2 ${isHi ? 'font-hindi-serif' : ''}`}>
                {isHi ? 'जीवन में स्पष्टता' : 'Life With Clarity'}
              </h3>
              <p className={`text-brand-text-soft text-sm mb-6 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                {isHi ? 'हर कदम पर ब्रह्मांडीय मार्गदर्शन।' : 'Cosmic guidance at every step.'}
              </p>
              <ul className="space-y-3.5">
                {AFTER.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-maroon text-brand-gold-light flex items-center justify-center mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span className={`text-brand-text-dark text-sm md:text-base ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                      {isHi ? item.hi : item.en}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Center arrow + CTA */}
        <div className="mt-14 max-w-2xl mx-auto text-center">
          <GoldDivider />
          <p className={`text-brand-text-soft text-base md:text-lg my-5 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
            {isHi
              ? 'सिर्फ ₹99 में पाएं अपना transformation roadmap — 60+ पेज की विस्तृत कुंडली रिपोर्ट।'
              : 'Get your transformation roadmap for just ₹99 — 60+ page detailed Kundli report.'}
          </p>
          <PremiumButton size="lg" onClick={onOpenForm}>
            <ShieldCheck className="w-4 h-4" />
            <span className={isHi ? 'font-hindi' : 'font-outfit'}>
              {isHi ? 'मेरा भविष्य बदलना शुरू करें' : 'Start My Transformation'}
            </span>
            <ArrowRight className="w-4 h-4" />
          </PremiumButton>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
